type ParsedVersion = {
  core: [number, number, number];
  prerelease: string[];
};

const parseVersion = (version: string): ParsedVersion | null => {
  const match = version
    .trim()
    .match(/^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?/);
  if (!match) return null;

  return {
    core: [Number(match[1]), Number(match[2]), Number(match[3])],
    prerelease: match[4] ? match[4].split(".") : [],
  };
};

const comparePrereleasePart = (left: string, right: string): number => {
  const leftNumber = /^\d+$/.test(left) ? Number(left) : null;
  const rightNumber = /^\d+$/.test(right) ? Number(right) : null;

  if (leftNumber !== null && rightNumber !== null) {
    return Math.sign(leftNumber - rightNumber);
  }
  if (leftNumber !== null) return -1;
  if (rightNumber !== null) return 1;
  return left.localeCompare(right);
};

/**
 * Returns whether a project's Player version contains a feature introduced in
 * `requiredVersion`. Unknown/missing project versions stay enabled to preserve
 * the existing behavior for legacy project records.
 */
export const meetsPlayerVersion = (
  projectVersion: string | null | undefined,
  requiredVersion: string | null | undefined
): boolean => {
  if (!requiredVersion || !projectVersion) return true;

  const project = parseVersion(projectVersion);
  const required = parseVersion(requiredVersion);
  if (!project || !required) return false;

  for (let index = 0; index < project.core.length; index += 1) {
    if (project.core[index] !== required.core[index]) {
      return project.core[index] > required.core[index];
    }
  }

  if (!project.prerelease.length && !required.prerelease.length) return true;
  if (!project.prerelease.length) return true;
  if (!required.prerelease.length) return false;

  const partCount = Math.max(
    project.prerelease.length,
    required.prerelease.length
  );
  for (let index = 0; index < partCount; index += 1) {
    const projectPart = project.prerelease[index];
    const requiredPart = required.prerelease[index];
    if (projectPart === undefined) return false;
    if (requiredPart === undefined) return true;
    if (projectPart !== requiredPart) {
      return comparePrereleasePart(projectPart, requiredPart) >= 0;
    }
  }

  return true;
};

export const getPlayerVersionRequirementMessage = (
  projectVersion: string | null | undefined,
  requiredVersion: string | null | undefined
): string => {
  const currentVersion = projectVersion || "unknown";
  const minimumVersion = requiredVersion || "a newer version";

  return `Requires Player ${minimumVersion}; this project uses ${currentVersion}. Rerender the project with a newer Player version to enable this connector.`;
};
